import * as React from 'react';

import { SlCheckbox } from '@shoelace-style/shoelace/dist/react';
import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { useAtom } from 'jotai';

import { FeatureUseEventAdapter } from '~analytics/adapters/FeatureUseEventAdapter';
import { translate } from '~l10n/client';
import { Badge } from '~shared/components/Badge';
import { SmallButton } from '~shared/components/buttons';
import { useAutoFocus } from '~shared/components/helpers';
import { SelectNumberRadioGroup } from '~shared/components/SelectNumberRadio/SelectNumberRadio';
import { toast } from '~shared/components/Toast';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import { TooltipV2 } from '~shared/components/TooltipV2';
import { FileDrop } from '~teacher/components/FileDrop/FileDrop';
import { FileDropConfig } from '~teacher/components/FileDrop/hooks/useFileDropConfig';
import { AiAssistedBadgeLink } from '~teacher/components/QuizEditor/AiAssistedBadgeLink';
import { InformationIcon } from '~teacher/components/QuizEditor/GenerateQuestionDialog/InformationIcon';
import { checkIsPromptEmpty } from '~teacher/components/QuizEditor/GenerateQuestionDialog/utils';
import { EightyPercentQuotaInlineMessage } from '~teacher/components/QuotaInlineMessage/EightyPercentQuotaInlineMessage';
import { HundredPercentQuotaInlineMessage } from '~teacher/components/QuotaInlineMessage/HundredPercentQuotaInlineMessage';
import { useAiQuotaWarning } from '~teacher/hooks/useAiQuotaWarning';
import { proCheck } from '~teacher/util/proCheck';

import {
  checkIsQuestionTypeEmpty,
  generateQuestionAtom,
  generateQuestionPaywallAtom,
  promptAtom,
  questionCountOptions,
  useScopedStepAtom,
} from './atom';
import { GenerateQuestionPaywall } from './GenerateQuestionPaywall';
import { GenerateQuestionPrompt } from './GenerateQuestionPrompt';
import { useHandleGenerateQuestions } from './useHandleGenerateQuestion';

interface UseGenerateQuestionParams {
  fileKey: string | null;
  fileType: string | null;
  fileSize: number | null;
}

function useGenerateQuestion({
  fileKey,
  fileType,
  fileSize,
}: UseGenerateQuestionParams) {
  const [, changeStep] = useScopedStepAtom();
  const handleGenerateQuestions = useHandleGenerateQuestions({
    fileKey,
    fileType,
    fileSize,
  });

  const [
    {
      [QuestionTypes.MultipleChoice]: multipleChoice,
      [QuestionTypes.TrueFalse]: trueFalse,
      [QuestionTypes.FreeResponse]: shortAnswers,
    },
  ] = useAtom(generateQuestionAtom);

  const [prompt] = useAtom(promptAtom);
  const [, setPaywallOpen] = useAtom(generateQuestionPaywallAtom);

  const isPro = proCheck.isPro();

  return () => {
    if (
      checkIsQuestionTypeEmpty(multipleChoice, trueFalse, shortAnswers) ||
      (checkIsPromptEmpty(prompt) && !fileKey)
    ) {
      toast.critical(translate('Required field(s) missing'));
      return;
    }

    if (!isPro && fileKey) {
      setPaywallOpen(true);
      return;
    }

    handleGenerateQuestions();
    changeStep({ type: 'NextStep' });
  };
}

type GenerateQuestionStep1Props = FileDropConfig;

export function GenerateQuestionStep1({
  tasks,
  addFileTasks,
  removeFileTasks,
  retryFileTasks,
  ...fileDropConfig
}: GenerateQuestionStep1Props) {
  const autoFocus = useAutoFocus();
  const {
    level: quotaLevel,
    resetDate,
    percentUsed,
  } = useAiQuotaWarning('Generate');
  const [warningDismissed, setWarningDismissed] = React.useState(false);
  const [
    {
      [QuestionTypes.MultipleChoice]: multipleChoice,
      [QuestionTypes.TrueFalse]: trueFalse,
      [QuestionTypes.FreeResponse]: shortAnswers,
      questionCount,
      includeExplanation,
    },
    dispatch,
  ] = useAtom(generateQuestionAtom);

  const isPro = proCheck.isPro();

  // fileKey is derived from task results — single source of truth, no stale atom state
  const fileKey = tasks[0]?.result?.fileKey ?? null;
  const fileType = tasks[0]?.file?.type ?? null;
  const fileSize = tasks[0]?.file?.size ?? null;

  const generateQuestion = useGenerateQuestion({ fileKey, fileType, fileSize });

  return (
    <>
      <GenerateQuestionPaywall />
      <div className="pb-4">
        <div className="flex">
          <div className="flex flex-shrink flex-col">
            <div className="flex">
              <AiAssistedBadgeLink
                onClick={() => {
                  new FeatureUseEventAdapter({
                    feature: 'ai_assisted_link',
                    source: 'generate_questions_step_1',
                    action: 'click',
                  }).sendAnalytics();
                }}
              />
            </div>
            <div className="font-display text-md leading-normal text-blue-900 font-700">
              {translate('Generate Questions')}
            </div>
            <div className="text-sans text-base leading-normal text-blue-600">
              {translate(
                'Create questions using a prompt and/or a file upload.'
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-5">
        <div
          className="flex justify-between flex-col pt-4 gap-2"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <GenerateQuestionPrompt ref={autoFocus} />
        </div>
        <div
          className="flex flex-col gap-4 pt-5"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <div className="flex items-center gap-3">
            <FileDrop
              helperText={
                <FileDrop.HelperText>
                  {translate(
                    'Add context to your prompt by uploading a worksheet, report, excerpt, example quiz, or another learning material.'
                  )}
                </FileDrop.HelperText>
              }
              label={
                <FileDrop.Label
                  label={translate('Attach Document')}
                  badge={
                    <>
                      {isPro ? null : (
                        <Badge tone="pro">{translate('Pro')}</Badge>
                      )}
                    </>
                  }
                />
              }
              fileTasks={tasks}
              addFileTasks={addFileTasks}
              removeFileTasks={removeFileTasks}
              retryFileTasks={retryFileTasks}
              {...fileDropConfig}
            />
          </div>
        </div>
        <div
          className="flex flex-col gap-6 pt-5"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <div className="font-sans text-base text-blue-900">
            {translate('Question Type')}
          </div>
          <div className="flex flex-col gap-4 font-sans text-base">
            <SlCheckbox
              size="medium"
              onSlChange={(event: any) => {
                dispatch({
                  type: 'QuestionTypeChange',
                  payload: {
                    questionType: QuestionTypes.MultipleChoice,
                    value: event.target.checked,
                  },
                });
              }}
              checked={multipleChoice}
              name={QuestionTypes.MultipleChoice}
            >
              <span>{translate('Multiple Choice')}</span>
            </SlCheckbox>
            <SlCheckbox
              size="medium"
              onSlChange={(event: any) => {
                dispatch({
                  type: 'QuestionTypeChange',
                  payload: {
                    questionType: QuestionTypes.TrueFalse,
                    value: event.target.checked,
                  },
                });
              }}
              checked={trueFalse}
              name={QuestionTypes.TrueFalse}
            >
              <span>{translate('True / False')}</span>
            </SlCheckbox>
            <SlCheckbox
              size="medium"
              onSlChange={(event: any) => {
                dispatch({
                  type: 'QuestionTypeChange',
                  payload: {
                    questionType: QuestionTypes.FreeResponse,
                    value: event.target.checked,
                  },
                });
              }}
              checked={shortAnswers}
              name={QuestionTypes.FreeResponse}
            >
              <span>{translate('Short Answer')}</span>
            </SlCheckbox>
          </div>
        </div>
        <div className="flex justify-between items-center gap-2 pt-5 pr-5 shadow-inset-border-t">
          <div className="flex flex-row items-center gap-2">
            <label
              className="text-base text-blue-900"
              htmlFor="generate-explanations-toggle"
              id="generate-explanations-label"
            >
              {translate('Generate explanations')}{' '}
            </label>
            <TooltipV2
              mode="click"
              contentWidth={260}
              label={translate(
                'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
              )}
            >
              <InformationIcon aria-hidden="true" />
            </TooltipV2>
          </div>
          <span id="generate-explanations-description" className="sr-only">
            {translate(
              'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
            )}
          </span>
          <ToggleSwitch
            labelledBy="generate-explanations-label"
            describedBy="generate-explanations-description"
            id="generate-explanations-toggle"
            size="small"
            on={includeExplanation}
            onChange={(value) => {
              dispatch({
                type: 'IncludeExplanationChange',
                payload: { includeExplanations: value },
              });
            }}
          />
        </div>
        <div className="pt-5 pr-5 shadow-inset-border-t">
          <div className="flex flex-row items-center justify-between gap-2">
            <label
              htmlFor="number-of-questions"
              className="text-base text-blue-900 leading-8"
              id="number-of-questions-label"
            >
              {translate('Number of Questions')}
            </label>
            <SelectNumberRadioGroup
              aria-labelledby={'number-of-questions-label'}
              name="numberOfQuestions"
              selectedNumber={questionCount}
              numberOptions={questionCountOptions}
              onChange={(number) => {
                dispatch({
                  type: 'NumberQuestionChange',
                  payload: { value: number },
                });
              }}
            />
          </div>
        </div>

        <div
          className="flex justify-end items-center h-24 gap-4 pt-8"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          {quotaLevel === 'warning' && !warningDismissed ? (
            <EightyPercentQuotaInlineMessage
              bucket="Generate"
              upgradeHref="#pricing"
              onClose={() => setWarningDismissed(true)}
              percentUsed={percentUsed}
            />
          ) : null}
          {quotaLevel === 'exceeded' ? (
            <HundredPercentQuotaInlineMessage
              bucket="Generate"
              upgradeHref="#pricing"
              limitResetDate={resetDate}
              percentUsed={percentUsed}
            />
          ) : null}
          <div>
            <SmallButton
              appearance="primary"
              onClick={generateQuestion}
              isDisabled={quotaLevel === 'exceeded'}
            >
              {translate('Generate')}
            </SmallButton>
          </div>
        </div>
      </div>
    </>
  );
}
